import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { Copy, Check, Plug, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
  "mont-aro": {
    label: "Mont-Aro",
    domain: "mont-aro.de",
    hint: "Alle Ausgangsbelege gehen automatisch an die zugewiesene Firma.",
    tenants_hint: null,
  },
  "eurostay": {
    label: "EuroStay",
    domain: "eurostay.world",
    hint: "Multi-Tenant-SaaS. Nur Belege für freigegebene Tenant-IDs werden akzeptiert.",
    tenants_hint: "Kommagetrennt. Beispiel: Goldenbed",
  },
  "mobigast": {
    label: "MobiGast",
    domain: "mobigast.de",
    hint: "Alle Ausgangsbelege gehen automatisch an die zugewiesene Firma.",
    tenants_hint: null,
  },
};

function CurlExample({ source, cfg, apiBase }) {
  const [copied, setCopied] = useState(false);
  const tenant = (cfg.allowed_tenant_ids || [])[0];
  const curl = [
    `curl -X POST "${apiBase}/api/external/${source}/documents" \\`,
    `  -H "X-API-Key: <DEIN_API_KEY>" \\`,
    `  -F "file=@rechnung.pdf" \\`,
    `  -F "document_type=invoice"  # oder credit_note, cancellation \\`,
    `  -F "invoice_number=RE-2026-0001" \\`,
    `  -F "invoice_date=2026-06-15" \\`,
    `  -F "total_amount=1234.56" \\`,
    `  -F "customer_reference=Kunde GmbH" \\`,
    `  -F "external_id=uuid-in-quell-app" \\`,
    source === "eurostay" ? `  -F "tenant_id=${tenant || "Goldenbed"}"` : null,
  ].filter(Boolean).join("\n");
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-[#5C5E60] font-semibold">Beispiel-Aufruf</div>
        <button
          onClick={() => { navigator.clipboard.writeText(curl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          data-testid={`copy-curl-${source}`}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-[#F3F3F1] hover:bg-[#E6E6E4]">
          {copied ? <><Check className="h-3 w-3" /> Kopiert</> : <><Copy className="h-3 w-3" /> Kopieren</>}
        </button>
      </div>
      <pre className="text-[11px] bg-[#0E1512] text-[#D9E1DD] p-3 rounded-md overflow-x-auto font-mono leading-relaxed">
        {curl}
      </pre>
    </div>
  );
}

export default function ExternalSources() {
  const [sources, setSources] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: c }] = await Promise.all([
        api.get("/external-sources"),
        api.get("/companies"),
      ]);
      setSources(s.sort((a, b) => a.source_app.localeCompare(b.source_app)));
      setCompanies(c);
    } catch {
      toast.error("Konnte Schnittstellen nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (source, patch) => {
    try {
      await api.patch(`/external-sources/${source}`, patch);
      toast.success("Aktualisiert");
      load();
    } catch (err) {
      toast.error("Fehler: " + formatDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-sm text-[#5C5E60]">Lade Schnittstellen…</div>;

  return (
    <div className="space-y-4" data-testid="external-sources-panel">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] font-semibold">
        <Plug className="h-3.5 w-3.5" /> App-Anbindungen (Mont-Aro · EuroStay · MobiGast)
      </div>

      {sources.map((s) => {
        const meta = SOURCE_META[s.source_app] || { label: s.source_app };
        const open = expanded === s.source_app;
        const tenantsStr = (s.allowed_tenant_ids || []).join(", ");
        return (
          <div key={s.source_app} className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden" data-testid={`source-${s.source_app}`}>
            <button
              onClick={() => setExpanded(open ? null : s.source_app)}
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-[#F9F9F8]">
              <div className="flex items-center gap-3">
                <div className="text-left">
                  <div className="font-display font-bold text-[#1A1A1A]">{meta.label}</div>
                  <div className="text-xs text-[#5C5E60] mt-0.5">{meta.domain}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-xs text-right">
                  <div className="text-[#5C5E60]">Firma:</div>
                  <div className="font-semibold text-[#1A1A1A]">{s.company_name || <span className="text-[#B31D1D]">nicht zugewiesen</span>}</div>
                </div>
                {s.source_app === "eurostay" && (
                  <div className="text-xs text-right">
                    <div className="text-[#5C5E60]">Tenants:</div>
                    <div className="font-mono text-[#1A1A1A]">{tenantsStr || <span className="text-[#B31D1D]">leer</span>}</div>
                  </div>
                )}
                {open ? <ChevronUp className="h-4 w-4 text-[#5C5E60]" /> : <ChevronDown className="h-4 w-4 text-[#5C5E60]" />}
              </div>
            </button>

            {open && (
              <div className="border-t border-[#E6E6E4] px-5 py-4 space-y-3 bg-[#FAFAF8]">
                <div className="text-xs text-[#5C5E60]">{meta.hint}</div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">Zugewiesene Firma</label>
                    <select
                      value={s.company_id || ""}
                      onChange={(e) => save(s.source_app, { company_id: e.target.value })}
                      data-testid={`source-company-${s.source_app}`}
                      className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white">
                      <option value="">— wählen —</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {s.source_app === "eurostay" && (
                    <div>
                      <label className="block text-xs font-semibold text-[#1A1A1A] mb-1">
                        Erlaubte Tenant-IDs *
                      </label>
                      <input
                        defaultValue={tenantsStr}
                        onBlur={(e) => {
                          const parts = e.target.value.split(",").map((x) => x.trim()).filter(Boolean);
                          save(s.source_app, { allowed_tenant_ids: parts });
                        }}
                        data-testid={`source-tenants-${s.source_app}`}
                        placeholder="Goldenbed"
                        className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]" />
                      <div className="text-[11px] text-[#5C5E60] mt-1">{meta.tenants_hint}</div>
                    </div>
                  )}
                </div>

                <label className="inline-flex items-center gap-2 text-xs mt-2">
                  <input
                    type="checkbox"
                    checked={s.enabled !== false}
                    onChange={(e) => save(s.source_app, { enabled: e.target.checked })}
                    data-testid={`source-enabled-${s.source_app}`}
                  />
                  Schnittstelle aktiv (empfängt Belege)
                </label>

                <CurlExample source={s.source_app} cfg={s} apiBase={apiBase} />

                <div className="text-[11px] text-[#5C5E60] leading-relaxed border-t border-[#E6E6E4] pt-3 mt-2">
                  <strong>Endpunkt:</strong> <code className="bg-[#F3F3F1] px-1.5 py-0.5 rounded font-mono">POST /api/external/{s.source_app}/documents</code><br />
                  <strong>Auth:</strong> Header <code className="bg-[#F3F3F1] px-1.5 py-0.5 rounded font-mono">X-API-Key</code> mit einem der API-Keys unten<br />
                  <strong>Erforderliche Felder:</strong> file, document_type (invoice/credit_note/cancellation), invoice_number, invoice_date, total_amount
                  {s.source_app === "eurostay" && <>, <strong>tenant_id</strong></>}<br />
                  <strong>Optional:</strong> customer_reference, net_amount, tax_amount, tax_rate, currency, external_id (Idempotenz), metadata_json
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
